import { Status } from './status-chip';
import StatusLabel, { StatusType } from './status-label';
import SharedToolbarFormStepper, {
  SharedToolbarFormStepperProps,
} from './shared-toolbar-form-stepper';
import { FieldValues } from 'react-hook-form';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import HelpOutline from '@mui/icons-material/HelpOutline';

interface SharedToolbarFormPanelProps<T extends FieldValues> {
  title: string;
  lastSavedDate?: number;
  isSaving?: boolean;
  status?: Status;
  primaryButtons?: React.ReactNode;
  stepperProps: SharedToolbarFormStepperProps<T>;
  onNeedHelp?: () => void;
  showHelp?: boolean;
}

export default function SharedToolbarFormPanel<T extends FieldValues>({
  title,
  lastSavedDate,
  isSaving,
  status,
  primaryButtons,
  stepperProps,
  onNeedHelp,
  showHelp,
}: SharedToolbarFormPanelProps<T>) {
  const theme = useTheme();

  const statusValue = (status?.toUpperCase() as StatusType) ?? 'UNKNOWN';
  return (
    <Stack
      sx={{
        position: 'sticky',
        top: 40,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      {/* Title */}
      <Card
        sx={{
          p: 1.5,
          flexShrink: 0,
          mb: 1,
          borderRadius: '8px',
          boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.12)',
        }}
      >
        <Stack
          direction="row"
          spacing={1.8}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: theme.palette.semantic.text.heading,
                letterSpacing: '0.15px',
                mr: 2,
              }}
            >
              {title}
            </Typography>

            <Divider flexItem orientation="vertical" />

            <SharedToolbarFormStepper {...stepperProps} />
          </Stack>
          <Stack direction="row" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2} mr={2}>
              {isSaving ? (
                <>
                  <CircularProgress
                    size={16}
                    sx={{ color: theme.palette.text.primary }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                    }}
                  >
                    Saving...
                  </Typography>
                </>
              ) : lastSavedDate && lastSavedDate > 0 ? (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                    }}
                  >
                    Last saved:{' '}
                    {new Date(lastSavedDate).toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Typography>
                </>
              ) : null}
              {statusValue !== 'UNKNOWN' ? (
                <>
                  <StatusLabel status={statusValue} />
                </>
              ) : null}
            </Stack>
            {onNeedHelp ? (
              <>
                {statusValue !== 'UNKNOWN' && (
                  <Divider flexItem orientation="vertical" />
                )}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={onNeedHelp}
                  aria-expanded={showHelp}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    border: 'none',
                    background: 'none',
                    p: 0,
                    cursor: 'pointer',
                    color: showHelp
                      ? theme.palette.semantic.hyperlink.disabled
                      : theme.palette.semantic.hyperlink.active,
                    mx: 2,
                  }}
                >
                  Need Help
                  <HelpOutline sx={{ fontSize: 18 }} />
                </Link>
              </>
            ) : null}
            <Divider flexItem orientation="vertical" />
            <Stack direction="row" alignItems="center" spacing={1.5} ml={1}>
              {primaryButtons}
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
